/* eslint-disable max-len, no-param-reassign */
import {
  useEffect, useRef, useCallback,
} from 'react';
import I18n from 'i18next';
import hljs from 'highlight.js';
import { useHistory } from 'react-router-dom';
import { globalInfoStore } from '@/models/global';

export function usePersistFn(fn: Function) {
  const ref = useRef<any>(() => {
    throw new Error('Cannot call an event handler while rendering.');
  });

  ref.current = fn;

  const persist = useCallback(
    (...args) => {
      const refFn = ref.current;
      if (refFn) {
        return refFn(...args);
      }
    },
    [ref],
  );

  return persist;
}

export function useGlobalLoadingEffect<T extends Function>(func: T, options?: { onError: Function }): [boolean, T] {
  const loadingFunc: any = usePersistFn(async (...args: any[]) => {
    globalInfoStore.loading = true;
    try {
      // eslint-disable-next-line prefer-spread
      const result = await func.apply(null, args);
      return result;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);

      if (options?.onError) {
        options.onError(err);
      }
    } finally {
      globalInfoStore.loading = false;
    }
  });
  return [globalInfoStore.loading, loadingFunc];
}

export const useUpdateEffect: typeof useEffect = (effect, deps) => {
  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else {
      return effect();
    }
  }, deps);
};

export const useUrlQuery = () => {
  const { location } = useHistory();
  const params = (location.search.split('?')[1] || '').split('&');
  const obj: any = {};
  params.forEach((key: string) => {
    const [objKey, objVal] = key.split('=');
    obj[objKey] = objVal;
  });
  return obj;
};

export const useUrlSerach = (search: string, searchObj: any) => {
  const params = (search.split('?')[1] || '').split('&');
  let obj: any = {};
  if (params) {
    params.forEach((key: string) => {
      const [objKey, objVal] = key.split('=');
      obj[objKey] = objVal;
    });
  }

  obj = { ...obj, ...searchObj };
  Object.keys(obj).forEach((key) => {
    if (obj[key] === '') {
      delete obj[key];
    }
  });
  return toQueryString(obj);
};

function toQueryPair(key: string, value: any) {
  if (typeof value === 'undefined') {
    return key;
  }
  return `${key}=${encodeURIComponent(value === null ? '' : String(value))}`;
}

function toQueryString(obj: any) {
  let ret: any = [];
  Object.keys(obj).forEach((keys) => {
    const key = encodeURIComponent(keys);
    if (!key) return;
    const values = obj[keys];
    if (values && values.constructor === Array) { // 数组
      const queryValues = [];
      for (let i = 0, len = values.length, value; i < len; i++) {
        value = values[i];
        queryValues.push(toQueryPair(key, value));
      }
      ret = ret.concat(queryValues);
    } else { // 字符串
      ret.push(toQueryPair(key, values));
    }
  });
  return ret.join('&');
}

export function useTranslateTextarea(ref?: Element, change?: Function) {
  // 不是编辑状态 修改内容 直接return
  if (!ref?.classList.contains('isEdit')) {
    const container = Array.from((ref || document).querySelectorAll('.translate-container') || []);
    container.forEach((dom) => {
      const text = (dom.querySelector('textarea') as Element).innerHTML;
      dom.innerHTML = text || '';
    });
    return;
  }

  const arr = Array.from((ref || document).querySelectorAll('textarea') || []);
  arr.forEach((dom: HTMLElement) => {
    const status = dom.getAttribute('data-status');
    (dom as any).style.height = `${(dom as Element).scrollHeight}px`;
    const subling = dom.nextElementSibling;
    const svg = '<?xml version="1.0" encoding="UTF-8"?><svg width="16" height="16" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg"><g><g><rect fill-opacity="0.01" fill="#FFFFFF" x="0" y="0" width="48" height="48" stroke-width="3" stroke="none" fill-rule="evenodd"/><path d="M24,44 C29.5228475,44 34.5228475,41.7614237 38.1421356,38.1421356 C41.7614237,34.5228475 44,29.5228475 44,24 C44,18.4771525 41.7614237,13.4771525 38.1421356,9.85786438 C34.5228475,6.23857625 29.5228475,4 24,4 C18.4771525,4 13.4771525,6.23857625 9.85786438,9.85786438 C6.23857625,13.4771525 4,18.4771525 4,24 C4,29.5228475 6.23857625,34.5228475 9.85786438,38.1421356 C13.4771525,41.7614237 18.4771525,44 24,44 Z" stroke="#FAAD15" stroke-width="3" fill="#FAAD15" fill-rule="nonzero" stroke-linejoin="round"/><g transform="translate(24.500000, 23.000000) scale(1, -1) translate(-24.500000, -23.000000) translate(21.000000, 11.000000)"><g><circle fill="#FFF" cx="3" cy="21.5" r="2.5" stroke-width="3" stroke="none" fill-rule="evenodd"/><g transform="translate(0.000000, 0.500000)"><polyline points="3.5 0.5 3.5 14.5 2.5 14.5 1.5 14.5" stroke-linecap="round" stroke-linejoin="round" stroke-width="3" stroke="#FFF" fill="none" fill-rule="evenodd"/><path d="M0,0.5 L7,0.5" stroke-linecap="round" stroke-linejoin="round" stroke-width="3" stroke="#FFF" fill="none" fill-rule="evenodd"/></g></g></g></g></g></svg>';
    const svg1 = '<?xml version="1.0" encoding="UTF-8"?><svg width="16" height="16" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg"><g><g><rect fill-opacity="0.01" fill="#FFFFFF" x="0" y="0" width="48" height="48" stroke-width="3" stroke="none" fill-rule="evenodd"/><path d="M24,44 C29.5228475,44 34.5228475,41.7614237 38.1421356,38.1421356 C41.7614237,34.5228475 44,29.5228475 44,24 C44,18.4771525 41.7614237,13.4771525 38.1421356,9.85786438 C34.5228475,6.23857625 29.5228475,4 24,4 C18.4771525,4 13.4771525,6.23857625 9.85786438,9.85786438 C6.23857625,13.4771525 4,18.4771525 4,24 C4,29.5228475 6.23857625,34.5228475 9.85786438,38.1421356 C13.4771525,41.7614237 18.4771525,44 24,44 Z" stroke="#338aff" stroke-width="3" fill="#338aff" fill-rule="nonzero" stroke-linejoin="round"/><g transform="translate(24.500000, 23.000000) scale(1, -1) translate(-24.500000, -23.000000) translate(21.000000, 11.000000)"><g><circle fill="#FFF" cx="3" cy="21.5" r="2.5" stroke-width="3" stroke="none" fill-rule="evenodd"/><g transform="translate(0.000000, 0.500000)"><polyline points="3.5 0.5 3.5 14.5 2.5 14.5 1.5 14.5" stroke-linecap="round" stroke-linejoin="round" stroke-width="3" stroke="#FFF" fill="none" fill-rule="evenodd"/><path d="M0,0.5 L7,0.5" stroke-linecap="round" stroke-linejoin="round" stroke-width="3" stroke="#FFF" fill="none" fill-rule="evenodd"/></g></g></g></g></g></svg>';
    (subling as HTMLElement).innerHTML = status === '0' ? svg : svg1;
    const tips = status === '0' ? I18n.t('translate_has_change') : I18n.t('translate_none_change');
    subling?.addEventListener('mouseover', () => {
      const { x, y, height } = (subling as Element).getBoundingClientRect();
      let oldDiv = document.getElementById('textarea-tip-show');
      if (!oldDiv) {
        oldDiv = document.createElement('div');
        oldDiv.id = 'textarea-tip-show';
        document.body.appendChild(oldDiv);
      }
      oldDiv.innerHTML = tips;
      oldDiv.style.display = 'block';
      oldDiv.style.top = `${y + height / 2}px`;
      oldDiv.style.left = `${x}px`;
    });

    subling?.addEventListener('mouseout', () => {
      const oldDiv = document.getElementById('textarea-tip-show');
      if (oldDiv) {
        oldDiv.style.display = 'none';
      }
    });
    dom.addEventListener('input', (e) => {
      (e.target as any).style.height = 'auto';
      (e.target as any).style.height = `${(e.target as Element).scrollHeight}px`;
      const name = (e.currentTarget as Element).getAttribute('data-name');
      const context = (e.currentTarget as Element).getAttribute('data-context');
      const { value } = e.target as any;
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      change && change({ name, context, value });
    });
    dom.addEventListener('focus', (e) => {
      const origin = (e.currentTarget as Element).getAttribute('data-origin');
      const { x, y, width } = (e.currentTarget as Element).getBoundingClientRect();
      let oldDiv = document.getElementById('textarea-tip-id');
      if (!oldDiv) {
        oldDiv = document.createElement('div');
        oldDiv.id = 'textarea-tip-id';
        document.body.appendChild(oldDiv);
      }
      oldDiv.style.display = 'block';
      (oldDiv as HTMLDivElement).innerHTML = `<div>${I18n.t('origin_text')}</div>${origin ? `<div>${origin}</div>` : ''}`;
      oldDiv.style.top = `${y}px`;
      oldDiv.style.left = `${x}px`;
      oldDiv.style.maxWidth = `${width}px`;
    });
    dom.addEventListener('blur', (e) => {
      const oldDiv = document.getElementById('textarea-tip-id');
      if (oldDiv) {
        oldDiv.style.display = 'none';
      }
      (e.target as any).style.height = 'auto';
      (e.target as any).style.height = `${(e.target as Element).scrollHeight}px`;
    });
  });
  (ref || document).addEventListener('scroll', () => {
    const oldDiv = document.getElementById('textarea-tip-id');
    const tip = document.getElementById('textarea-tip-show');
    if (oldDiv) {
      oldDiv.style.display = 'none';
    }
    if (tip) {
      tip.style.display = 'none';
    }
  });
  if (ref) {
    document.addEventListener('scroll', () => {
      const oldDiv = document.getElementById('textarea-tip-id');
      const tip = document.getElementById('textarea-tip-show');
      if (oldDiv) {
        oldDiv.style.display = 'none';
      }
      if (tip) {
        tip.style.display = 'none';
      }
    });
  }
}

export function useCodeHightlight(ref: HTMLDivElement) {
  ref.querySelectorAll('pre code').forEach((block) => {
    hljs.highlightBlock(block as HTMLElement);
  });
}
